// API Service Layer for Heapster ID
// Provides consistent interface for both Firestore operations and future HTTP API endpoints
// Supports the API design from PRD Section 5.5 (Public API)

import { doc, getDoc, collection, query, getDocs } from 'firebase/firestore';
import type {
  QueryConstraint,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { appConfig } from '../lib/env';
import type { UserProfile } from '../lib/types/user';
import { handleFirebaseError } from '../lib/firebase-errors';
import {
  measureAsync,
  recordUserFlow,
  UserFlowMetrics,
} from '../lib/performance';

// API Response wrapper for consistent error handling
export interface ApiResponse<T> {
  data?: T;
  error?: string;
  success: boolean;
}

// Generic API service class
class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = appConfig.api.baseUrl;
  }

  // Generic HTTP request method (for future API endpoints)
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const method = options.method || 'GET';
    const metricName = `api_${method.toLowerCase()}_${endpoint.replace(/\//g, '_')}`;

    recordUserFlow(UserFlowMetrics.API_CALL_START, {
      endpoint,
      method,
    });

    try {
      // Add 30-second timeout to prevent infinite hangs
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      let response;
      try {
        response = await measureAsync(metricName, async () =>
          fetch(`${this.baseUrl}${endpoint}`, {
            headers: {
              'Content-Type': 'application/json',
              ...options.headers,
            },
            ...options,
            signal: controller.signal,
          })
        );
        clearTimeout(timeoutId);
      } catch (error) {
        clearTimeout(timeoutId);
        // Ask the controller, not the error: an abort rejects with a
        // DOMException, which does not satisfy `instanceof Error` across
        // realms (#1744).
        if (controller.signal.aborted) {
          throw new Error(`Request timeout after 30s: ${endpoint}`);
        }
        throw error;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      recordUserFlow(UserFlowMetrics.API_CALL_SUCCESS, {
        endpoint,
        method,
        status: response.status,
      });
      return { data, success: true };
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      recordUserFlow(UserFlowMetrics.API_CALL_ERROR, {
        endpoint,
        method,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return {
        error: error instanceof Error ? error.message : 'Unknown error',
        success: false,
      };
    }
  }

  // HTTP GET method
  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  // HTTP POST method
  async post<T>(endpoint: string, data: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // HTTP PATCH method
  async patch<T>(endpoint: string, data: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // HTTP DELETE method
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// Firestore service for direct database reads. It has no write methods:
// every write goes through the HTTP API.
class FirestoreService {
  // Get document by ID
  async getDocument<T>(
    collectionPath: string,
    docId: string
  ): Promise<ApiResponse<T>> {
    try {
      const docRef = doc(db, collectionPath, docId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          data: { id: docSnap.id, ...docSnap.data() } as T,
          success: true,
        };
      } else {
        return {
          error: 'Document not found',
          success: false,
        };
      }
    } catch (error) {
      const friendlyError = handleFirebaseError(error);
      return {
        error: friendlyError.message,
        success: false,
      };
    }
  }

  // Query collection with filters
  async queryCollection<T>(
    collectionPath: string,
    constraints: QueryConstraint[] = []
  ): Promise<ApiResponse<T[]>> {
    try {
      const q = query(collection(db, collectionPath), ...constraints);
      const querySnapshot = await getDocs(q);

      const data = querySnapshot.docs.map((doc: QueryDocumentSnapshot) => ({
        id: doc.id,
        ...doc.data(),
      })) as T[];

      return { data, success: true };
    } catch (error) {
      const friendlyError = handleFirebaseError(error);
      return {
        error: friendlyError.message,
        success: false,
      };
    }
  }
}

// User-specific service methods
class UserService {
  private firestore = new FirestoreService();
  private api = new ApiService();

  // Get user profile (Firestore for now, HTTP API in future)
  async getUserProfile(uid: string): Promise<ApiResponse<UserProfile>> {
    return this.firestore.getDocument<UserProfile>('users', uid);
  }

  // Update user profile via HTTP API (PUT /v1/users/{uid}/profile)
  async updateUserProfile(
    uid: string,
    updates: Partial<UserProfile>
  ): Promise<ApiResponse<any>> {
    // Import authenticatedApi at the top of the file to avoid circular dependency
    const { authenticatedApi } = await import('../lib/authenticated-api');

    // Convert UserProfile updates to API format
    const apiUpdates: any = {};

    if (updates.display_name !== undefined) {
      apiUpdates.display_name = updates.display_name;
    }

    if (updates.photo_url !== undefined) {
      apiUpdates.photo_url = updates.photo_url;
    }

    const response = await authenticatedApi.updateUserProfile(
      uid,
      apiUpdates as {
        display_name?: string;
        photo_url?: {
          '128'?: string;
          '512'?: string;
        };
      }
    );

    // Convert API response to match expected format
    if (response.success) {
      return { success: true };
    } else {
      return { success: false, error: response.error };
    }
  }

  // Future: Get user profile via HTTP API (when Cloud Functions are ready)
  async getUserProfileAPI(uid: string): Promise<ApiResponse<UserProfile>> {
    return this.api.get<UserProfile>(`/v1/users/${uid}`);
  }
}

// Subscription service (for future implementation)
class SubscriptionService {
  private firestore = new FirestoreService();
  private api = new ApiService();

  // Get user subscription
  async getUserSubscription(uid: string): Promise<ApiResponse<any>> {
    return this.firestore.getDocument('entitlements', uid);
  }

  // Future: Start checkout (PRD Section 5.5)
  async startCheckoutAPI(data: any): Promise<ApiResponse<any>> {
    return this.api.post<any>('/v1/checkout', data);
  }
}

// Export service instances
export const userService = new UserService();
export const subscriptionService = new SubscriptionService();

// Export service classes for advanced usage
export { ApiService, FirestoreService };
